/**
 * FilterStore - Centralized state management for Cells Page filters
 *
 * Uses Preact Signals for reactive state with automatic URL sync and localStorage persistence
 */

import { computed, effect, signal } from '@preact/signals'
import { safeLocal } from '../utils/safeStorage.js'
import { trackCompareAction, trackFilter } from '../modules/dataService.js'
import {
    computeAllDynamicRangeData,
    computeCrossFilterCounts
} from '../modules/filterCountUtils.js'
import { hasAddedDateData } from '../utils/cellSort.js'
import {
  allCells,
  basis,
  sortBy,
  sortOrder,
  tableSortBy,
  tableSortOrder,
  freePlotXAxis,
  freePlotYAxis,
  freePlotColorBy,
  highlightedCellIds,
} from './globalSignals.js'
import { consumeIonBasisReloadExpectation } from './ionReloadGuard.js'
// Aliased: filterStore has its own `activePreset` for FILTER presets, and
// plotPresets.js has a same-named signal for GRAPH presets. Two different
// concepts, one name — importing it unaliased is a redeclaration error, which
// is the only reason the collision is visible at all.
import {
  activePreset as activePlotPreset,
  plotMode,
} from './plotPresets.js'

// ============================================================================
// FILTER STATE
// ============================================================================

// Basis selection for energy/power/capacity/current (abs, grav, vol)
export const filterBasis = signal('grav') // 'abs' | 'grav' | 'vol'

// Categorical filters
export const selectedChemistries = signal([]) // Legacy: combined (for backward compat)
export const selectedCathodeChemistries = signal([]) // Cathode: NMC, NCA, LFP, etc.
export const selectedAnodeChemistries = signal([]) // Anode: Graphite, Silicon, LTO, etc.
export const selectedManufacturers = signal([])
export const selectedCountries = signal([])
export const selectedFormFactors = signal([])
export const selectedTechnologies = signal([]) // Li-Ion, LiPo, etc.

/**
 * Restriction to an explicit set of product ids (D4).
 *
 * The one constraint the UI genuinely cannot express: "exactly these seven
 * cells". Every other filter names a PROPERTY and lets the catalogue decide who
 * matches; this one names the members. Ion needs it because some answers are an
 * enumeration — "the five most recently added", a shortlist carried over from
 * the guided flow — and no combination of facets and ranges reproduces an
 * arbitrary set.
 *
 * `null` means inactive (NOT an empty Set, which would legitimately mean "no
 * cells match" and blank the Explorer). It composes with the other filters as an
 * INTERSECTION, so a facet the user then ticks narrows the set further rather
 * than fighting it.
 *
 * The label is not decoration: a chip reading "7 cells" tells the user nothing
 * about why, and this is the one filter they cannot reverse-engineer from the
 * grid. Ion supplies the sentence it used ("the 5 most recently added cells").
 */
export const ionCellIdSet = signal(null)
export const ionCellIdSetLabel = signal('')

/** Drop the id restriction. Also the chip's remove handler. */
export function clearIonCellSet() {
  ionCellIdSet.value = null
  ionCellIdSetLabel.value = ''
}

// Range filters (wide open by default to show all cells)
// Use very high max values that will be replaced by actual data ranges
export const energyRange = signal({ min: 0, max: 999999 })
export const powerRange = signal({ min: 0, max: 999999 })
export const capacityRange = signal({ min: 0, max: 999999 })
export const currentRange = signal({ min: 0, max: 999999 }) // Current (A)
export const cyclesRange = signal({ min: 0, max: 50000 })
export const yearRange = signal({ min: 1990, max: 2030 })
export const batchVariationRange = signal({ min: 0, max: 100 }) // CAP_spread_std_rel in %
export const agingRange = signal({ min: 0, max: 100 }) // CAP_loss_rel in %

// Full range values (actual min/max from all cells) - used to determine if filter is active
export const fullRangeEnergy = signal({ min: 0, max: 999999 })
export const fullRangePower = signal({ min: 0, max: 999999 })
export const fullRangeCapacity = signal({ min: 0, max: 999999 })
export const fullRangeCurrent = signal({ min: 0, max: 999999 })
export const fullRangeCycles = signal({ min: 0, max: 50000 })
export const fullRangeYear = signal({ min: 1990, max: 2030 })
export const fullRangeBatchVariation = signal({ min: 0, max: 100 })
export const fullRangeAging = signal({ min: 0, max: 100 })

// Search & preset
export const searchQuery = signal('')
export const savedSearchTerms = signal([]) // Array of saved search strings
export const activePreset = signal(null)

// Pending range overrides from presets — applied after initFilterRangesFromMetadata
// to survive async metadata reload triggered by basis/domain changes.
export const pendingRangeOverrides = signal(null)

// Advanced filters
export const showCertifiedOnly = signal(false)
export const showInProductionOnly = signal(false)
export const showFullDataOnly = signal(false)

// ============================================================================
// VIEW STATE
// ============================================================================

// Current view mode
export const currentView = signal('grid') // 'grid' | 'ragone' | 'table'

// Grid view settings
// sortBy/sortOrder now owned exclusively by globalSignals.js (imported above) —
// see refactor(ion): consolidate duplicate sort signals to globalSignals.
export const displayMode = signal('detailed') // 'compact' | 'detailed' | 'minimal'
export const groupBy = signal('none') // 'none' | 'chemistry' | 'manufacturer' | 'form-factor' | 'year'

// ---------------------------------------------------------------------------
// 'addedDate' sort availability — the sort key whose data can be absent
// ---------------------------------------------------------------------------
//
// `createdAt` (catalogue-insertion date) only reaches the Explorer through the
// PRIMARY /explorer/v2 payload. `MainAppPage`'s legacy fallback load
// (getProductList + batchGetCells → getCellNormalized) builds cells from the
// per-cell DETAIL response, which has no catalogue-insertion field at all — so
// on that path every `createdAt` is null, the comparator is a total tie, and
// picking "Added to catalogue" would reorder NOTHING while looking like it
// worked. Silence is the bug; these two signals are the cure:
//
//   `addedDateSortAvailable`  → the dropdowns hide the option when false
//   `addedDateSortDowngraded` → the bar says so when we had to drop the choice
//
// Reactive to the loaded data on purpose (NOT a build flag): the same session
// can start on the primary path and fall back later, and vice versa.

/** True iff at least one loaded cell carries a parseable catalogue-insertion date. */
export const addedDateSortAvailable = computed(() => hasAddedDateData(allCells.value))

/** True after we replaced a requested 'addedDate' sort because the data lacks it. */
export const addedDateSortDowngraded = signal(false)

/** What an unsupported 'addedDate' sort falls back to (a key every path can serve). */
export const ADDED_DATE_FALLBACK_SORT = 'name'

// Enforce the above. `sortBy` may arrive as 'addedDate' from a deep link
// (`?sort=addedDate`, which Ion can now emit — see loadFiltersFromURL) or from
// localStorage on a later visit; `tableSortBy` likewise. We cannot judge either
// at parse time, because no cells are loaded yet — hence an effect that waits
// for data and only then decides.
effect(() => {
  const cells = allCells.value
  // Nothing loaded yet → not "missing", just "not here yet". Deciding now would
  // strip a perfectly valid deep-link sort before the first page even arrives.
  if (!Array.isArray(cells) || cells.length === 0) return

  if (addedDateSortAvailable.value) {
    addedDateSortDowngraded.value = false
    return
  }

  let downgraded = false
  if (sortBy.value === 'addedDate') {
    sortBy.value = ADDED_DATE_FALLBACK_SORT
    downgraded = true
  }
  if (tableSortBy.value === 'addedDate') {
    tableSortBy.value = ADDED_DATE_FALLBACK_SORT
    downgraded = true
  }
  if (downgraded) {
    console.warn(
      '[FilterStore] "Added to catalogue" sort dropped: the loaded catalogue carries no ' +
        'insertion dates (legacy fallback load). Sorting by ' + ADDED_DATE_FALLBACK_SORT + '.'
    )
    addedDateSortDowngraded.value = true
  }
})

// Graph view settings
export const ragoneColorBy = signal('chemistry') // 'chemistry' | 'manufacturer' | 'form-factor'
export const ragoneSizeBy = signal('none') // 'none' | 'capacity' | 'voltage' | 'year'
export const ragoneShowLabels = signal(false) // boolean: show cell names on plot

// Table view settings
export const visibleColumns = signal([
  'name',
  'chemistry',
  'manufacturer',
  'energy_grav',
  'power_grav',
  'capacity',
  'form_factor'
])
export const tableDensity = signal('normal') // 'compact' | 'normal' | 'comfortable'
export const selectedRows = signal([])

// Panel state
export const filterPanelCollapsed = signal(false)
export const expandedSections = signal(['format', 'energy', 'power'])

// Compare mode state
export const panelMode = signal('filter') // 'filter' | 'compare'
export const compareCells = signal([]) // Array of cell IDs (max 5)
export const hiddenCells = signal([]) // Array of cell IDs temporarily hidden from plots

// ============================================================================
// COMPUTED VALUES
// ============================================================================

/**
 * Active filters for display as chips
 */
export const activeFilters = computed(() => {
  const filters = []

  // Ion's id restriction, first because it is the most consequential: it can
  // shrink 300 cells to 5, and unlike every other chip the user cannot deduce it
  // from looking at the grid. Counted like any other filter so activeFilterCount
  // (and anything showing "N filters active") does not under-report it.
  if (ionCellIdSet.value) {
    const n = ionCellIdSet.value.size
    filters.push({
      type: 'ion-cell-set',
      label: ionCellIdSetLabel.value || `${n} selected cell${n === 1 ? '' : 's'}`,
      value: n,
      display: ionCellIdSetLabel.value || `${n} cells`,
    })
  }

  // Cathode Chemistry filters (NEW)
  if (selectedCathodeChemistries.value.length > 0) {
    selectedCathodeChemistries.value.forEach(chem => {
      filters.push({
        type: 'cathode-chemistry',
        label: `Cathode: ${chem}`,
        value: chem,
        display: chem
      })
    })
  }

  // Anode Chemistry filters (NEW)
  if (selectedAnodeChemistries.value.length > 0) {
    selectedAnodeChemistries.value.forEach(chem => {
      filters.push({
        type: 'anode-chemistry',
        label: `Anode: ${chem}`,
        value: chem,
        display: chem
      })
    })
  }

  // Legacy Chemistry filters (for backward compat)
  if (selectedChemistries.value.length > 0) {
    selectedChemistries.value.forEach(chem => {
      filters.push({
        type: 'chemistry',
        label: chem,
        value: chem,
        display: chem
      })
    })
  }

  if (selectedManufacturers.value.length > 0) {
    selectedManufacturers.value.forEach(mfr => {
      filters.push({
        type: 'manufacturer',
        label: mfr,
        value: mfr,
        display: mfr
      })
    })
  }

  if (selectedCountries.value.length > 0) {
    selectedCountries.value.forEach(country => {
      filters.push({
        type: 'country',
        label: country,
        value: country,
        display: country
      })
    })
  }

  if (selectedFormFactors.value.length > 0) {
    selectedFormFactors.value.forEach(form => {
      filters.push({
        type: 'form-factor',
        label: form,
        value: form,
        display: form
      })
    })
  }

  if (selectedTechnologies.value.length > 0) {
    selectedTechnologies.value.forEach(tech => {
      filters.push({
        type: 'technology',
        label: tech,
        value: tech,
        display: tech
      })
    })
  }

  // Range filters - only show as active if different from full range
  // IMPORTANT: Only show as active if fullRange is initialized (not default values)
  // Each range has its own initialization check to prevent false positives during data loading

  // Energy: initialized when max < 999000 (not default 999999)
  const isEnergyInitialized = fullRangeEnergy.value.max < 999000
  if (isEnergyInitialized && (energyRange.value.min !== fullRangeEnergy.value.min || energyRange.value.max !== fullRangeEnergy.value.max)) {
    filters.push({
      type: 'energy',
      label: 'Energy',
      value: energyRange.value,
      display: `Energy: ${energyRange.value.min}-${energyRange.value.max} Wh/kg`
    })
  }

  // Power: initialized when max < 999000 (not default 999999)
  const isPowerInitialized = fullRangePower.value.max < 999000
  if (isPowerInitialized && (powerRange.value.min !== fullRangePower.value.min || powerRange.value.max !== fullRangePower.value.max)) {
    filters.push({
      type: 'power',
      label: 'Power',
      value: powerRange.value,
      display: `Power: ${powerRange.value.min}-${powerRange.value.max} W/kg`
    })
  }

  // Capacity: initialized when max < 999000 (not default 999999)
  const isCapacityInitialized = fullRangeCapacity.value.max < 999000
  if (isCapacityInitialized && (capacityRange.value.min !== fullRangeCapacity.value.min || capacityRange.value.max !== fullRangeCapacity.value.max)) {
    filters.push({
      type: 'capacity',
      label: 'Capacity',
      value: capacityRange.value,
      display: `Capacity: ${capacityRange.value.min}-${capacityRange.value.max} Ah`
    })
  }

  // Current: initialized when max < 999000 (not default 999999)
  const isCurrentInitialized = fullRangeCurrent.value.max < 999000
  if (isCurrentInitialized && (currentRange.value.min !== fullRangeCurrent.value.min || currentRange.value.max !== fullRangeCurrent.value.max)) {
    filters.push({
      type: 'current',
      label: 'Current',
      value: currentRange.value,
      display: `Current: ${currentRange.value.min}-${currentRange.value.max} A`
    })
  }

  // Year: initialized when min > 1990 or max < 2030 (not default range)
  const isYearInitialized = fullRangeYear.value.min > 1990 || fullRangeYear.value.max < 2030
  if (isYearInitialized && (yearRange.value.min !== fullRangeYear.value.min || yearRange.value.max !== fullRangeYear.value.max)) {
    filters.push({
      type: 'year',
      label: 'Year',
      value: yearRange.value,
      display: `Year: ${yearRange.value.min}-${yearRange.value.max}`
    })
  }

  // Batch Variation: initialized when max < 99 (not default 100)
  const isBatchVarInitialized = fullRangeBatchVariation.value.max < 99
  if (isBatchVarInitialized && (batchVariationRange.value.min !== fullRangeBatchVariation.value.min || batchVariationRange.value.max !== fullRangeBatchVariation.value.max)) {
    filters.push({
      type: 'batchVariation',
      label: 'Cell-to-Cell Variation',
      value: batchVariationRange.value,
      display: `Batch Var: ${batchVariationRange.value.min}-${batchVariationRange.value.max}%`
    })
  }

  // Aging: initialized when max < 99 (not default 100)
  const isAgingInitialized = fullRangeAging.value.max < 99
  if (isAgingInitialized && (agingRange.value.min !== fullRangeAging.value.min || agingRange.value.max !== fullRangeAging.value.max)) {
    filters.push({
      type: 'aging',
      label: 'Post-Formation Loss',
      value: agingRange.value,
      display: `Aging: ${agingRange.value.min}-${agingRange.value.max}%`
    })
  }

  // Search query
  if (searchQuery.value.trim()) {
    filters.push({
      type: 'search',
      label: 'Search',
      value: searchQuery.value,
      display: `"${searchQuery.value}"`
    })
  }

  // Advanced filters
  if (showCertifiedOnly.value) {
    filters.push({
      type: 'certified',
      label: 'Certified',
      value: true,
      display: 'Certified only'
    })
  }

  if (showInProductionOnly.value) {
    filters.push({
      type: 'production',
      label: 'In Production',
      value: true,
      display: 'In production'
    })
  }

  return filters
})

/**
 * Computed values: active filters count
 */
export const activeFilterCount = computed(() => activeFilters.value.length)

/**
 * Whether any filters are active
 */
export const hasActiveFilters = computed(() => activeFilterCount.value > 0)

/**
 * Whether compare mode has cells
 */
export const hasCompareCells = computed(() => compareCells.value.length > 0)

/**
 * Whether compare mode is ready (at least 2 cells)
 */
export const canCompare = computed(() => compareCells.value.length >= 2)

/**
 * Number of cells in comparison
 */
export const compareCount = computed(() => compareCells.value.length)

// ============================================================================
// DYNAMIC FILTER COUNTS (Faceted Search)
// ============================================================================

/**
 * Current filter state object for cross-filter computation
 * Aggregates all active filter values into a single object
 */
export const currentFilterState = computed(() => {
  const currentBasis = basis.value

  // Determine if range filters are active (different from full range)
  const isEnergyActive = fullRangeEnergy.value.max < 999000 &&
    (energyRange.value.min > fullRangeEnergy.value.min || energyRange.value.max < fullRangeEnergy.value.max)
  const isPowerActive = fullRangePower.value.max < 999000 &&
    (powerRange.value.min > fullRangePower.value.min || powerRange.value.max < fullRangePower.value.max)
  const isCapacityActive = fullRangeCapacity.value.max < 999000 &&
    (capacityRange.value.min > fullRangeCapacity.value.min || capacityRange.value.max < fullRangeCapacity.value.max)
  const isCurrentActive = fullRangeCurrent.value.max < 999000 &&
    (currentRange.value.min > fullRangeCurrent.value.min || currentRange.value.max < fullRangeCurrent.value.max)
  const isYearActive = fullRangeYear.value.min > 1990 &&
    (yearRange.value.min > fullRangeYear.value.min || yearRange.value.max < fullRangeYear.value.max)
  const isBatchVarActive = fullRangeBatchVariation.value.max < 99 &&
    (batchVariationRange.value.min > fullRangeBatchVariation.value.min || batchVariationRange.value.max < fullRangeBatchVariation.value.max)
  const isAgingActive = fullRangeAging.value.max < 99 &&
    (agingRange.value.min > fullRangeAging.value.min || agingRange.value.max < fullRangeAging.value.max)

  return {
    cathodeChemistries: selectedCathodeChemistries.value,
    anodeChemistries: selectedAnodeChemistries.value,
    manufacturers: selectedManufacturers.value,
    formFactors: selectedFormFactors.value,
    technologies: selectedTechnologies.value,
    countries: selectedCountries.value,
    energy: { ...energyRange.value, active: isEnergyActive },
    power: { ...powerRange.value, active: isPowerActive },
    capacity: { ...capacityRange.value, active: isCapacityActive },
    current: { ...currentRange.value, active: isCurrentActive },
    year: { ...yearRange.value, active: isYearActive },
    batchVariation: { ...batchVariationRange.value, active: isBatchVarActive },
    aging: { ...agingRange.value, active: isAgingActive }
  }
})

/**
 * Cells used for counts/ranges in the filter sidebar.
 *
 * Includes the FULL catalogue — locked (demo shadow) cells too — so the facet
 * counts reflect the true "how many cells have this value" market picture next
 * to each filter option, even when a hidden-on-ghost filter (chemistry /
 * technology / country / …) is active. Locked cells still carry their
 * categorical data in the response, so they contribute to categorical counts;
 * hidden numeric RANGE facets keep their own locked-cell gate downstream
 * (computeAllDynamicRangeData). This affects the displayed COUNTS only — locked
 * cells stay locked in the grid; the lock/unlock behaviour is unchanged.
 */
const cellsForFacets = computed(() => {
  const cells = allCells.value
  if (!cells || cells.length === 0) return []
  return cells
})

/**
 * Dynamic filter counts - recalculated when any filter changes
 * For each filter category, shows how many cells would match if that option was selected
 * (considering all OTHER currently active filters).
 */
export const dynamicFilterCounts = computed(() => {
  const cells = cellsForFacets.value
  const filters = currentFilterState.value
  const currentBasis = basis.value

  if (!cells || cells.length === 0) {
    return {
      cathodeChemistries: new Map(),
      anodeChemistries: new Map(),
      manufacturers: new Map(),
      formFactors: new Map(),
      technologies: new Map(),
      countries: new Map()
    }
  }

  return computeCrossFilterCounts(cells, filters, currentBasis)
})

/**
 * `?chemistry=` values from a URL, waiting for the facet options to arrive.
 *
 * Not a filter itself — a parked instruction. Emptied by the effect below, which
 * is the only writer besides `loadFiltersFromURL`.
 */
export const pendingUrlChemistries = signal([])

/**
 * Place parked `?chemistry=` values into the facet list that actually filters.
 *
 * WHY THIS CAN FAIL TO PLACE THEM, and why that is the honest outcome: cathode
 * and anode are AND-combined by the grid. A request that spans both — "LFP or
 * LTO", one value from each list — is a UNION the Explorer cannot express;
 * routing LFP to cathode and LTO to anode asks for cells that are both and
 * returns none. So a mixed request applies NO filter and says so in the console,
 * rather than showing a confident subset that is missing a chemistry (LFP alone
 * would have been 29 of the 32 cells the link named).
 *
 * Single-facet requests — which is what "filter to LFP cells" and almost every
 * real link is — now filter correctly instead of not at all.
 */
/**
 * Split loose "chemistry" values across the two facets that actually filter.
 *
 * Exported because there are TWO callers that must agree: the URL reader (via
 * the effect below) and Ion's live `set_filters` (commandLayer's
 * `applyFilterCommand`). They disagreed once already — the URL path was fixed
 * while the live path still routed every value into the cathode list, so
 * "LFP and LTO" showed 29 cells with LTO silently gone, which is what BA
 * reported.
 *
 * @returns {{cathode: string[], anode: string[], unplaced: string[],
 *   ready: boolean, spansBoth: boolean}} `ready: false` means the facet options
 *   are not loaded yet and the caller should wait rather than conclude anything.
 */
export function routeChemistryValues(values) {
  const counts = dynamicFilterCounts.value
  const cathodeOptions = [...(counts?.cathodeChemistries?.keys() || [])]
  const anodeOptions = [...(counts?.anodeChemistries?.keys() || [])]
  if (cathodeOptions.length === 0 && anodeOptions.length === 0) {
    return { cathode: [], anode: [], unplaced: [], ready: false, spansBoth: false }
  }

  // ALL matches, not the first, and the same three-step widening
  // `resolveFacetValues` uses: "NMC" has to fan out to BOTH "NMC (high-nickel)"
  // and "NMC (low-nickel)", which is what a user does by ticking every NMC row.
  // Returning one match regressed exactly that (caught by
  // tests/explorer-commands/facet-value-resolution.test.js).
  const matchAll = (options, value) => {
    const want = value.toLowerCase()
    let m = options.filter((o) => o.toLowerCase() === want)
    if (!m.length) m = options.filter((o) => o.toLowerCase().includes(want))
    if (!m.length) m = options.filter((o) => want.includes(o.toLowerCase()))
    return m
  }

  const cathode = []
  const anode = []
  const unplaced = []
  const push = (bucket, vals) => {
    for (const v of vals) if (!bucket.includes(v)) bucket.push(v)
  }
  for (const value of values) {
    const asCathode = matchAll(cathodeOptions, value)
    if (asCathode.length) {
      push(cathode, asCathode)
      continue
    }
    const asAnode = matchAll(anodeOptions, value)
    if (asAnode.length) {
      push(anode, asAnode)
      continue
    }
    // Kept, not dropped: an unmatched value stays as a visible chip that filters
    // to an honest 0, which is the established contract (a silently ignored
    // filter is worse than a visibly empty result). Reported alongside so the
    // caller can decide, but it counts as CATHODE for placement — the
    // pre-routing default — so this cannot flip a single-value request into the
    // "spans both facets" branch.
    unplaced.push(value)
    push(cathode, [value])
  }
  return {
    cathode,
    anode,
    unplaced,
    ready: true,
    spansBoth: cathode.length > 0 && anode.length > 0,
  }
}

/**
 * Cell ids matching ANY of the given chemistry values, on either facet.
 *
 * The union the facet panel cannot express: cathode and anode are AND-combined,
 * so "LFP or LTO" (one value from each list) has no filter form. Resolved here
 * against the loaded catalogue so a caller can hand it to the Ion cell set,
 * which exists precisely for a selection the facets cannot describe.
 *
 * Field precedence mirrors `filterCountUtils.computeCrossFilterCounts`, or the
 * union would disagree with the counts shown next to each option.
 */
export function cellIdsForChemistries(values) {
  const wanted = values.map((v) => String(v).toLowerCase())
  const hit = (group) => group && wanted.some((w) => group.toLowerCase().includes(w))
  return (allCells.value || [])
    .filter((cell) => {
      const cathodeGroup =
        cell.chemical?.cathode_material_group ||
        cell.chemical?.main_chemistry ||
        cell.chemical?.cathode
      const anodeGroup = cell.chemical?.anode_material_group || cell.chemical?.anode
      return hit(cathodeGroup) || hit(anodeGroup)
    })
    .map((cell) => cell.id)
    .filter(Boolean)
}

effect(() => {
  const pending = pendingUrlChemistries.value
  if (!pending.length) return

  const routed = routeChemistryValues(pending)
  // Options not loaded yet — wait for the next run rather than concluding
  // "no match" from an empty list.
  if (!routed.ready) return

  if (routed.spansBoth) {
    // A union across both facets. Applied as an Ion-style cell SET rather than
    // refused: refusing showed the whole catalogue while the link named two
    // chemistries, and routing only the cathode half showed a confident subset
    // with one chemistry missing. The set is the only form that is actually the
    // thing asked for.
    const ids = cellIdsForChemistries(pending)
    if (ids.length) {
      ionCellIdSet.value = new Set(ids)
      ionCellIdSetLabel.value = pending.join(' & ')
    }
  } else if (routed.cathode.length) {
    selectedCathodeChemistries.value = routed.cathode
  } else if (routed.anode.length) {
    selectedAnodeChemistries.value = routed.anode
  }
  if (routed.unplaced.length) {
    console.warn(
      '[FilterStore] ?chemistry= values not found in either chemistry facet: ' +
        routed.unplaced.join(',')
    )
  }

  // Cleared either way: the instruction has been considered. Leaving it parked
  // would re-run this on every facet-count change, and re-applying a filter the
  // user has since removed by hand is worse than not applying it at all.
  pendingUrlChemistries.value = []
})

/**
 * Dynamic ranges and histograms for range sliders
 * Each slider's range is computed based on cells filtered by all OTHER filters.
 */
export const dynamicRangeData = computed(() => {
  const cells = cellsForFacets.value
  const filters = currentFilterState.value
  const currentBasis = basis.value

  if (!cells || cells.length === 0) {
    return {
      ranges: {},
      histograms: {}
    }
  }

  return computeAllDynamicRangeData(cells, filters, currentBasis)
})

// ============================================================================
// ACTIONS
// ============================================================================

// Handle for the pending post-reset resize armed by resetAllFilters().
//
// Same uncancelled-timer shape as the import-time resize effects in
// globalSignals.js (ff775c77) and switchSection()'s scroll restore: the
// timeout was armed bare and its handle thrown away. This one is reached
// through a call, and `resetAllFilters()` is what ~15 test files run in their
// `beforeEach` — so every one of them armed a live 50 ms timer, and any file
// that finished inside those 50 ms was torn down first. The timer then fired
// against a disposed `window`: vitest reports a process-level unhandled error
// with every test passing, exit code 1. That is the flake that turned !13 —
// thirteen inserted lines of CSS, zero JS — red on one run and green on a
// retry of the same SHA.
//
// Two changes, because the handle alone is not enough. Clearing a
// still-pending timer on re-entry collapses a burst of resets to ONE resize,
// which is also the correct UI behaviour; but the LAST timer of a file still
// outlives it. The `typeof window` check inside the callback is what makes
// that survivor harmless — `typeof` on an undeclared identifier does not
// throw, which is exactly the `ReferenceError: window is not defined` being
// guarded against.
let resetResizeTimer = null

/**
 * Reset all filters to default values
 */
export const resetAllFilters = () => {
  selectedChemistries.value = []
  selectedCathodeChemistries.value = []
  selectedAnodeChemistries.value = []
  selectedManufacturers.value = []
  selectedCountries.value = []
  selectedFormFactors.value = []
  selectedTechnologies.value = []

  // Use fullRange values (actual data min/max) instead of hardcoded defaults
  energyRange.value = { ...fullRangeEnergy.value }
  powerRange.value = { ...fullRangePower.value }
  capacityRange.value = { ...fullRangeCapacity.value }
  currentRange.value = { ...fullRangeCurrent.value }
  cyclesRange.value = { min: 100, max: 5000 }
  yearRange.value = { ...fullRangeYear.value }
  batchVariationRange.value = { ...fullRangeBatchVariation.value }
  agingRange.value = { ...fullRangeAging.value }

  searchQuery.value = ''
  savedSearchTerms.value = []
  activePreset.value = null
  pendingRangeOverrides.value = null

  showCertifiedOnly.value = false
  showInProductionOnly.value = false
  showFullDataOnly.value = false
  // Must be cleared here too, or "Clear all filters" leaves the Explorer stuck
  // on Ion's five cells with no visible reason — the exact "why do I only see
  // 5 cells" failure this filter risks.
  clearIonCellSet()
  // Ion's plot HIGHLIGHT goes as well. Strictly it is not a filter — it hides
  // nothing, it only marks — but "Clear all filters" is the button people press
  // when something on screen looks unexplained, and a leftover ring on a cell the
  // current view is no longer about is exactly that. Revert also restores it; this
  // is the escape hatch for someone who never noticed the Revert card.
  highlightedCellIds.value = null

  // Trigger resize so ECharts recalculates axes after filter data changes.
  // Supersede a still-pending resize from an earlier reset: a burst of resets
  // needs one resize, not one per reset.
  if (resetResizeTimer !== null) clearTimeout(resetResizeTimer)
  resetResizeTimer = setTimeout(() => {
    resetResizeTimer = null
    // The survivor guard — see the note above `resetResizeTimer`.
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
  }, 50)
}

/**
 * Remove a specific filter
 * @param {string} type - Filter type
 * @param {*} value - Filter value (for categorical filters)
 */
export const removeFilter = (type, value = null) => {
  switch (type) {
    case 'chemistry':
      selectedChemistries.value = selectedChemistries.value.filter(c => c !== value)
      break

    case 'cathode-chemistry':
      selectedCathodeChemistries.value = selectedCathodeChemistries.value.filter(c => c !== value)
      break

    case 'anode-chemistry':
      selectedAnodeChemistries.value = selectedAnodeChemistries.value.filter(c => c !== value)
      break

    case 'manufacturer':
      selectedManufacturers.value = selectedManufacturers.value.filter(m => m !== value)
      break

    case 'country':
      selectedCountries.value = selectedCountries.value.filter(c => c !== value)
      break

    case 'form-factor':
      selectedFormFactors.value = selectedFormFactors.value.filter(f => f !== value)
      break

    case 'technology':
      selectedTechnologies.value = selectedTechnologies.value.filter(t => t !== value)
      break

    case 'energy':
      energyRange.value = { ...fullRangeEnergy.value }
      break

    case 'power':
      powerRange.value = { ...fullRangePower.value }
      break

    case 'capacity':
      capacityRange.value = { ...fullRangeCapacity.value }
      break

    case 'cycles':
      cyclesRange.value = { min: 100, max: 5000 }
      break

    case 'year':
      yearRange.value = { ...fullRangeYear.value }
      break

    case 'batchVariation':
      batchVariationRange.value = { ...fullRangeBatchVariation.value }
      break

    case 'aging':
      agingRange.value = { ...fullRangeAging.value }
      break

    case 'search':
      searchQuery.value = ''
      break

    case 'certified':
      showCertifiedOnly.value = false
      break

    case 'production':
      showInProductionOnly.value = false
      break
  }

  // Clear preset if any filter is manually removed
  if (activePreset.value) {
    activePreset.value = null
  }
}

/**
 * Toggle a categorical filter value
 * @param {string} type - Filter type (chemistry, manufacturer, form-factor)
 * @param {string} value - Value to toggle
 */
export const toggleFilter = (type, value) => {
  switch (type) {
    case 'chemistry':
      if (selectedChemistries.value.includes(value)) {
        selectedChemistries.value = selectedChemistries.value.filter(c => c !== value)
      } else {
        selectedChemistries.value = [...selectedChemistries.value, value]
      }
      break

    case 'cathode-chemistry':
      if (selectedCathodeChemistries.value.includes(value)) {
        selectedCathodeChemistries.value = selectedCathodeChemistries.value.filter(c => c !== value)
      } else {
        selectedCathodeChemistries.value = [...selectedCathodeChemistries.value, value]
      }
      break

    case 'anode-chemistry':
      if (selectedAnodeChemistries.value.includes(value)) {
        selectedAnodeChemistries.value = selectedAnodeChemistries.value.filter(c => c !== value)
      } else {
        selectedAnodeChemistries.value = [...selectedAnodeChemistries.value, value]
      }
      break

    case 'manufacturer':
      if (selectedManufacturers.value.includes(value)) {
        selectedManufacturers.value = selectedManufacturers.value.filter(m => m !== value)
      } else {
        selectedManufacturers.value = [...selectedManufacturers.value, value]
      }
      break

    case 'country':
      if (selectedCountries.value.includes(value)) {
        selectedCountries.value = selectedCountries.value.filter(c => c !== value)
      } else {
        selectedCountries.value = [...selectedCountries.value, value]
      }
      break

    case 'form-factor':
      if (selectedFormFactors.value.includes(value)) {
        selectedFormFactors.value = selectedFormFactors.value.filter(f => f !== value)
      } else {
        selectedFormFactors.value = [...selectedFormFactors.value, value]
      }
      break

    case 'technology':
      if (selectedTechnologies.value.includes(value)) {
        selectedTechnologies.value = selectedTechnologies.value.filter(t => t !== value)
      } else {
        selectedTechnologies.value = [...selectedTechnologies.value, value]
      }
      break
  }

  // Clear preset
  activePreset.value = null

  // Track filter change for analytics
  trackFilter(type, value)
}

/**
 * Update a range filter
 * @param {string} type - Range filter type
 * @param {object} range - New range { min, max }
 */
export const updateRange = (type, range) => {
  switch (type) {
    case 'energy':
      energyRange.value = range
      break
    case 'power':
      powerRange.value = range
      break
    case 'capacity':
      capacityRange.value = range
      break
    case 'current':
      currentRange.value = range
      break
    case 'cycles':
      cyclesRange.value = range
      break
    case 'year':
      yearRange.value = range
      break
    case 'batchVariation':
      batchVariationRange.value = range
      break
    case 'aging':
      agingRange.value = range
      break
  }

  // Clear preset
  activePreset.value = null
}

/**
 * Apply a preset filter configuration
 * @param {string} presetId - Preset identifier
 */
export const applyPreset = (presetId) => {
  const presets = {
    'high-energy': {
      name: 'High Energy Cells',
      filters: {
        energyRange: { min: 200, max: 350 },
        selectedChemistries: ['NMC', 'NCA']
      }
    },
    'high-power': {
      name: 'High Power Cells',
      filters: {
        powerRange: { min: 2000, max: 5000 },
        selectedChemistries: ['NMC', 'LTO']
      }
    },
    'ev-grade': {
      name: 'EV Grade',
      filters: {
        energyRange: { min: 150, max: 280 },
        capacityRange: { min: 2.0, max: 10 },
        cyclesRange: { min: 1000, max: 5000 },
        selectedFormFactors: ['21700', 'Prismatic', 'Pouch'],
        showCertifiedOnly: true
      }
    },
    'energy-storage': {
      name: 'Energy Storage',
      filters: {
        selectedChemistries: ['LFP'],
        cyclesRange: { min: 3000, max: 5000 },
        capacityRange: { min: 50, max: 300 }
      }
    },
    'consumer': {
      name: 'Consumer Electronics',
      filters: {
        selectedFormFactors: ['18650', '21700'],
        capacityRange: { min: 2.0, max: 5.0 }
      }
    }
  }

  const preset = presets[presetId]
  if (!preset) return

  // Reset first
  resetAllFilters()

  // Apply preset filters
  const { filters } = preset
  if (filters.energyRange) energyRange.value = filters.energyRange
  if (filters.powerRange) powerRange.value = filters.powerRange
  if (filters.capacityRange) capacityRange.value = filters.capacityRange
  if (filters.cyclesRange) cyclesRange.value = filters.cyclesRange
  if (filters.yearRange) yearRange.value = filters.yearRange

  if (filters.selectedChemistries) selectedChemistries.value = filters.selectedChemistries
  if (filters.selectedManufacturers) selectedManufacturers.value = filters.selectedManufacturers
  if (filters.selectedFormFactors) {
    selectedFormFactors.value = filters.selectedFormFactors
  }

  if (filters.showCertifiedOnly) showCertifiedOnly.value = filters.showCertifiedOnly
  if (filters.showInProductionOnly) showInProductionOnly.value = filters.showInProductionOnly

  activePreset.value = presetId
}

/**
 * Toggle section expanded state
 * @param {string} sectionId - Section identifier
 */
export const toggleSection = (sectionId) => {
  if (expandedSections.value.includes(sectionId)) {
    expandedSections.value = expandedSections.value.filter(s => s !== sectionId)
  } else {
    expandedSections.value = [...expandedSections.value, sectionId]
  }
}

/**
 * Check if a section is expanded
 * @param {string} sectionId - Section identifier
 * @returns {boolean}
 */
export const isSectionExpanded = (sectionId) => {
  return expandedSections.value.includes(sectionId)
}

/**
 * Collapse all filter sections
 */
export const collapseAllSections = () => {
  expandedSections.value = []
}

/**
 * Expand all filter sections
 */
export const expandAllSections = () => {
  const allSections = ['chemistry', 'manufacturer', 'format', 'technology', 'energy', 'power', 'capacity', 'current', 'year', 'batchVariation', 'aging']
  expandedSections.value = allSections
}

// ============================================================================
// COMPARE ACTIONS
// ============================================================================

/**
 * Add a cell to comparison list
 * @param {string} cellId - Cell product ID
 * @returns {boolean} - Success status
 */
export const addToCompare = (cellId) => {
  if (compareCells.value.length >= 5) {
    console.warn('[filterStore] Maximum 5 cells for comparison')
    return false
  }

  if (compareCells.value.includes(cellId)) {
    // Already in cart
    return false
  }

  compareCells.value = [...compareCells.value, cellId]

  // Track compare action
  trackCompareAction('added', cellId)

  // Open compare panel when first cell added (but stay in current view)
  if (compareCells.value.length === 1) {
    panelMode.value = 'compare'
  }

  console.log('[filterStore] ✅ Added to compare:', cellId, `(${compareCells.value.length}/5)`)
  return true
}

/**
 * Remove a cell from comparison list
 * @param {string} cellId - Cell product ID
 */
export const removeFromCompare = (cellId) => {
  compareCells.value = compareCells.value.filter(id => id !== cellId)

  // Track compare action
  trackCompareAction('removed', cellId)

  // Close compare panel when last cell removed
  if (compareCells.value.length === 0) {
    panelMode.value = 'filter'
  }

  console.log('[filterStore] ❌ Removed from compare:', cellId, `(${compareCells.value.length}/5)`)
}

/**
 * Clear all cells from comparison
 */
export const clearCompare = () => {
  const count = compareCells.value.length
  compareCells.value = []
  panelMode.value = 'filter'
  console.log(`[filterStore] 🗑️ Cleared ${count} cells from comparison`)
}

/**
 * Toggle a cell in comparison list
 * @param {string} cellId - Cell product ID
 * @returns {boolean} - Whether cell is now in comparison (true) or removed (false)
 */
export const toggleCompare = (cellId) => {
  if (compareCells.value.includes(cellId)) {
    removeFromCompare(cellId)
    return false
  } else {
    return addToCompare(cellId)
  }
}

/**
 * Check if a cell is in comparison list
 * @param {string} cellId - Cell product ID
 * @returns {boolean}
 */
export const isInCompare = (cellId) => {
  return compareCells.value.includes(cellId)
}

/**
 * Toggle cell visibility in plots (hide/show)
 * @param {string} cellId - Cell product ID
 */
export const toggleCellVisibility = (cellId) => {
  if (hiddenCells.value.includes(cellId)) {
    // Show cell
    hiddenCells.value = hiddenCells.value.filter(id => id !== cellId)
    console.log('[filterStore] 👁️ Showing cell:', cellId)
  } else {
    // Hide cell
    hiddenCells.value = [...hiddenCells.value, cellId]
    console.log('[filterStore] 🙈 Hiding cell:', cellId)
  }
}

/**
 * Check if a cell is hidden
 * @param {string} cellId - Cell product ID
 * @returns {boolean}
 */
export const isCellHidden = (cellId) => {
  return hiddenCells.value.includes(cellId)
}

// ============================================================================
// URL SYNC
// ============================================================================

/**
 * Update URL from current filter state
 */
export const updateURLFromFilters = () => {
  const params = new URLSearchParams()

  // View
  if (currentView.value !== 'grid') {
    params.set('view', currentView.value)
  }

  // Categorical filters
  if (selectedChemistries.value.length > 0) {
    params.set('chemistry', selectedChemistries.value.join(','))
  }

  if (selectedManufacturers.value.length > 0) {
    params.set('mfr', selectedManufacturers.value.join(','))
  }

  if (selectedFormFactors.value.length > 0) {
    params.set('form', selectedFormFactors.value.join(','))
  }

  // Range filters (only if changed from full range)
  if (energyRange.value.min !== fullRangeEnergy.value.min || energyRange.value.max !== fullRangeEnergy.value.max) {
    params.set('energy', `${energyRange.value.min}-${energyRange.value.max}`)
  }

  if (powerRange.value.min !== fullRangePower.value.min || powerRange.value.max !== fullRangePower.value.max) {
    params.set('power', `${powerRange.value.min}-${powerRange.value.max}`)
  }

  if (capacityRange.value.min !== fullRangeCapacity.value.min || capacityRange.value.max !== fullRangeCapacity.value.max) {
    params.set('capacity', `${capacityRange.value.min}-${capacityRange.value.max}`)
  }

  // Search
  if (searchQuery.value.trim()) {
    params.set('q', searchQuery.value)
  }

  // Preset
  if (activePreset.value) {
    params.set('preset', activePreset.value)
  }

  // View-specific params
  if (currentView.value === 'grid') {
    if (sortBy.value !== 'energy') params.set('sort', sortBy.value)
    if (sortOrder.value !== 'desc') params.set('order', sortOrder.value)
    if (groupBy.value !== 'none') params.set('group', groupBy.value)
    if (displayMode.value !== 'detailed') params.set('display', displayMode.value)
  } else if (currentView.value === 'ragone') {
    if (ragoneColorBy.value !== 'chemistry') params.set('color', ragoneColorBy.value)
    if (ragoneSizeBy.value !== 'capacity') params.set('size', ragoneSizeBy.value)
  } else if (currentView.value === 'table') {
    if (tableDensity.value !== 'normal') params.set('density', tableDensity.value)
  }

  // Update URL without page reload
  const url = `/cells${params.toString() ? '?' + params.toString() : ''}`
  window.history.replaceState(null, '', url)
}

/**
 * Load filter state from URL parameters
 */
export const loadFiltersFromURL = () => {
  const params = new URLSearchParams(window.location.search)

  console.log('[FilterStore] 🔍 Loading filters from URL:', window.location.search)
  console.log('[FilterStore] 📊 URL params:', Object.fromEntries(params.entries()))

  // View
  const view = params.get('view')
  if (view && ['grid', 'graph', 'table'].includes(view)) {
    currentView.value = view
  }

  // Free-plot axes and colour-by from the URL (?x=&y=&color=).
  //
  // Read BEFORE the preset, because explicit axes have to win over one: the
  // preset effect in plotPresets.js writes `?preset=` into the URL whenever the
  // graph view is active, so a link carrying explicit axes can easily arrive
  // with a stale preset param alongside them — and applying the preset would
  // silently replace the very axes the link exists to set.
  //
  // This is what makes Ion's "Open in Graph view" button able to deliver the
  // plot it names, instead of the graph view with whatever axes the user last
  // had. It is also a plain feature: the graph view had no shareable deep link
  // except ?preset= before this.
  const urlX = params.get('x')
  const urlY = params.get('y')
  const urlColor = params.get('color')
  const hasExplicitPlot = !!(urlX || urlY || urlColor)
  if (hasExplicitPlot) {
    if (urlX) freePlotXAxis.value = urlX
    if (urlY) freePlotYAxis.value = urlY
    if (urlColor) freePlotColorBy.value = urlColor
    // Same two writes applyPlotCommand makes, for the same two reasons: the
    // settings drawer's mount effect would otherwise re-apply the stored GRAPH
    // preset over these axes, and the Axes card is expert-only so in Guided mode
    // the user could not see or adjust what the link just set.
    activePlotPreset.value = null
    plotMode.value = 'expert'
    // Nulling the signal is not enough on its own: the drawer's mount effect
    // reads `?preset=` from the URL DIRECTLY (plotPresets.readPresetFromURL) and
    // prefers it over the signal, so a stale param would win anyway. Strip it.
    try {
      const clean = new URL(window.location.href)
      if (clean.searchParams.has('preset')) {
        clean.searchParams.delete('preset')
        window.history.replaceState({}, '', clean.toString())
      }
    } catch (e) {
      console.warn('[FilterStore] could not strip stale ?preset=', e)
    }
  }

  // FILTER preset (a different concept from the graph preset above — this is
  // filterStore's own `applyPreset`, keyed by its own ids). Not gated on
  // hasExplicitPlot: axes and filters are independent, and a link may legitimately
  // carry both.
  const preset = params.get('preset')
  if (preset) {
    applyPreset(preset)
  }

  // Categorical filters
  // `?chemistry=` is Ion's single "chemistry" facet (`view_in_explorer` emits it),
  // but the Explorer has no main-chemistry filter: its Chemistry panel is a
  // CATHODE list (NCA, NMC high/low-nickel, LFP, LMO) and an ANODE list
  // (graphite, graphite-silicon, LTO), and `MainAppPage` AND-combines them.
  // `selectedChemistries` — where this used to land — is read by the grid only
  // for the magic value 'Analysis Ongoing', so the value produced a chip and
  // filtered nothing (measured on staging 2026-08-03: 321 cells, 2 active chips).
  //
  // Routing it needs the facet OPTION lists to know whether "LFP" is a cathode or
  // an anode value, and those come from the metadata fetch — which has not
  // happened yet when this runs at app init. So park the values and let the
  // effect below place them once the options exist. Same shape as the
  // `addedDateSortAvailable` effect further up: a decision that cannot be made at
  // parse time waits for its data instead of guessing.
  const chemistry = params.get('chemistry')
  if (chemistry) {
    pendingUrlChemistries.value = chemistry
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  }

  const cathode = params.get('cathode')
  if (cathode) {
    selectedCathodeChemistries.value = cathode.split(',')
  }

  const anode = params.get('anode')
  if (anode) {
    selectedAnodeChemistries.value = anode.split(',')
  }

  const mfr = params.get('mfr')
  if (mfr) {
    selectedManufacturers.value = mfr.split(',')
  }

  const form = params.get('form')
  if (form) {
    selectedFormFactors.value = form.split(',')
  }

  const country = params.get('country')
  if (country) {
    selectedCountries.value = country.split(',')
  }

  const tech = params.get('tech')
  if (tech) {
    selectedTechnologies.value = tech.split(',')
  }

  // Range filters — supports open-ended bounds:
  //   "200-300" → both bounds set
  //   "200-"    → min only (max stays at full-range default)
  //   "-300"    → max only
  // Ion's nav tools emit half-open ranges so the explorer's slider
  // stays at the actual data max when the AI hasn't constrained it.
  const parseRange = (raw) => {
    if (!raw) return null
    const [minRaw, maxRaw] = raw.split('-')
    const min = minRaw === '' ? null : Number(minRaw)
    const max = maxRaw === '' ? null : Number(maxRaw)
    if (min !== null && isNaN(min)) return null
    if (max !== null && isNaN(max)) return null
    return { min, max }
  }

  const applyRange = (raw, signalRef) => {
    const parsed = parseRange(raw)
    if (!parsed) return
    const current = signalRef.value
    signalRef.value = {
      min: parsed.min !== null ? parsed.min : current.min,
      max: parsed.max !== null ? parsed.max : current.max,
    }
  }

  applyRange(params.get('energy'), energyRange)
  applyRange(params.get('power'), powerRange)
  applyRange(params.get('capacity'), capacityRange)
  applyRange(params.get('current'), currentRange)
  applyRange(params.get('year'), yearRange)
  applyRange(params.get('cycles'), cyclesRange)

  // Search
  const q = params.get('q')
  if (q) {
    searchQuery.value = q
  }

  // Sort params. Written to BOTH the grid pair and the table pair, for the same
  // reason `applyViewCommand` (commandLayer.js) does: sort lives per view so a
  // user can hand-sort each one differently, but a sort arriving in a URL is a
  // cross-view intent — it came from a link (Ion's `view_in_explorer`, a shared
  // deep link), not from a click on one view's own header. Writing only the grid
  // pair meant `?view=table&sort=aging` landed in the table with the table's own
  // sort untouched: the link looked applied and silently wasn't.
  //
  // NOTE: `sort=addedDate` is accepted here unconditionally on purpose — no
  // cells are loaded at this point, so whether the catalogue can serve that key
  // is not yet knowable. The availability effect above (addedDateSortAvailable)
  // reverts it to ADDED_DATE_FALLBACK_SORT and flags the downgrade once the data
  // arrives, so the deep link either works or visibly degrades — never silently.
  const sort = params.get('sort')
  if (sort) {
    sortBy.value = sort
    tableSortBy.value = sort
  }

  const order = params.get('order')
  if (order && ['asc', 'desc'].includes(order)) {
    sortOrder.value = order
    tableSortOrder.value = order
  }

  const group = params.get('group')
  if (group) groupBy.value = group

  const display = params.get('display')
  if (display && ['compact', 'detailed', 'minimal'].includes(display)) {
    displayMode.value = display
  }

  // Ragone params
  const color = params.get('color')
  if (color) ragoneColorBy.value = color

  const size = params.get('size')
  if (size) ragoneSizeBy.value = size

  // Table params
  const density = params.get('density')
  if (density && ['compact', 'normal', 'comfortable'].includes(density)) {
    tableDensity.value = density
  }
}

// ============================================================================
// LOCALSTORAGE PERSISTENCE
// ============================================================================

const STORAGE_KEY = 'batemo-cells-state'

/**
 * Save current state to localStorage
 */
const saveToLocalStorage = () => {
  const state = {
    view: currentView.value,
    filters: {
      chemistry: selectedChemistries.value,
      manufacturers: selectedManufacturers.value,
      formFactors: selectedFormFactors.value,
      energyRange: energyRange.value,
      powerRange: powerRange.value,
      capacityRange: capacityRange.value,
      searchQuery: searchQuery.value
    },
    compare: {
      mode: panelMode.value,
      cells: compareCells.value
    },
    viewSettings: {
      grid: {
        displayMode: displayMode.value,
        groupBy: groupBy.value
      },
      ragone: {
        colorBy: ragoneColorBy.value,
        sizeBy: ragoneSizeBy.value
      },
      table: {
        density: tableDensity.value,
        columns: visibleColumns.value
      }
    },
    panelState: {
      collapsed: filterPanelCollapsed.value,
      expandedSections: expandedSections.value
    }
  }

  try {
    safeLocal.set(STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save state to localStorage:', e)
  }
}

/**
 * Load state from localStorage
 */
export const loadFromLocalStorage = () => {
  try {
    const stored = safeLocal.get(STORAGE_KEY)
    if (!stored) return

    const state = JSON.parse(stored)

    // Restore compare state
    if (state.compare) {
      panelMode.value = state.compare.mode || 'filter'
      if (state.compare.cells && Array.isArray(state.compare.cells)) {
        compareCells.value = state.compare.cells.slice(0, 5) // Max 5
      }
    }

    // Only restore view and panel state, not filters
    // (filters should come from URL for shareable links)
    if (state.viewSettings) {
      if (state.viewSettings.grid) {
        // sortBy/sortOrder intentionally NOT restored here anymore — globalSignals.js
        // owns that persistence exclusively (own localStorage keys). Restoring from this
        // legacy nested blob would clobber the canonical value on every app init.
        displayMode.value = state.viewSettings.grid.displayMode || 'detailed'
        groupBy.value = state.viewSettings.grid.groupBy || 'none'
      }

      if (state.viewSettings.ragone) {
        ragoneColorBy.value = state.viewSettings.ragone.colorBy || 'chemistry'
        ragoneSizeBy.value = state.viewSettings.ragone.sizeBy || 'capacity'
      }

      if (state.viewSettings.table) {
        tableDensity.value = state.viewSettings.table.density || 'normal'
        if (state.viewSettings.table.columns) {
          visibleColumns.value = state.viewSettings.table.columns
        }
      }
    }

    if (state.panelState) {
      filterPanelCollapsed.value = state.panelState.collapsed || false
      if (state.panelState.expandedSections) {
        expandedSections.value = state.panelState.expandedSections
      }
    }
  } catch (e) {
    console.warn('Failed to load state from localStorage:', e)
  }
}

// ============================================================================
// AUTO-SYNC EFFECTS
// ============================================================================

// NOTE: URL sync DISABLED to prevent routing loops
// Filters are now purely client-side state with localStorage persistence
// View switching is handled by router through wouter navigation

// Auto-sync URL when filters change (debounced) - DISABLED
// let urlSyncTimeout
// effect(() => {
//   // Access all signals to trigger on any change
//   currentView.value
//   selectedChemistries.value
//   selectedManufacturers.value
//   selectedFormFactors.value
//   energyRange.value
//   powerRange.value
//   capacityRange.value
//   searchQuery.value
//   sortBy.value
//   sortOrder.value
//   groupBy.value
//   displayMode.value
//   activePreset.value
//
//   // Debounce URL updates
//   clearTimeout(urlSyncTimeout)
//   urlSyncTimeout = setTimeout(() => {
//     updateURLFromFilters()
//   }, 300)
// })

// Auto-save to localStorage when state changes (debounced)
let storageSyncTimeout
effect(() => {
  // Access relevant signals
  currentView.value
  displayMode.value
  groupBy.value
  ragoneColorBy.value
  ragoneSizeBy.value
  tableDensity.value
  visibleColumns.value
  filterPanelCollapsed.value
  expandedSections.value
  panelMode.value
  compareCells.value

  // Debounce storage updates
  clearTimeout(storageSyncTimeout)
  storageSyncTimeout = setTimeout(() => {
    saveToLocalStorage()
  }, 1000)
})

// ============================================================================
// DEBUG HELPER (available immediately)
// ============================================================================

// Expose debug helper globally (available before initFilterStore)
if (typeof window !== 'undefined') {
  window.__BATEMO_FILTERS__ = {
    get activeFilters() { return activeFilters.value },
    get activeCount() { return activeFilterCount.value },
    get ranges() {
      return {
        energy: energyRange.value,
        power: powerRange.value,
        capacity: capacityRange.value,
        current: currentRange.value,
        year: yearRange.value,
        batchVariation: batchVariationRange.value,
        aging: agingRange.value
      }
    },
    get fullRanges() {
      return {
        energy: fullRangeEnergy.value,
        power: fullRangePower.value,
        capacity: fullRangeCapacity.value,
        current: fullRangeCurrent.value,
        year: fullRangeYear.value,
        batchVariation: fullRangeBatchVariation.value,
        aging: fullRangeAging.value
      }
    },
    get categorical() {
      return {
        chemistries: selectedChemistries.value,
        manufacturers: selectedManufacturers.value,
        formFactors: selectedFormFactors.value,
        technologies: selectedTechnologies.value
      }
    },
    get search() { return searchQuery.value },
    get isFullRangeInitialized() {
      return {
        energy: fullRangeEnergy.value.max < 999000,
        power: fullRangePower.value.max < 999000,
        capacity: fullRangeCapacity.value.max < 999000,
        current: fullRangeCurrent.value.max < 999000,
        year: fullRangeYear.value.min > 1990 || fullRangeYear.value.max < 2030,
        batchVariation: fullRangeBatchVariation.value.max < 99,
        aging: fullRangeAging.value.max < 99
      }
    },
    // Helper: Show all info
    info() {
      console.group('🔍 Batemo Filters Debug')
      console.log('✅ Active Filters:', this.activeFilters)
      console.log('📊 Count:', this.activeCount)
      console.log('📐 Current Ranges:', this.ranges)
      console.log('📏 Full Ranges:', this.fullRanges)
      console.log('🏷️ Categorical:', this.categorical)
      console.log('🔎 Search:', this.search)
      console.log('✔️ Range Initialized:', this.isFullRangeInitialized)
      console.groupEnd()
    },
    // Helper: Reset all filters
    reset() {
      resetAllFilters()
      console.log('✅ All filters reset')
    }
  }

  console.log('[FilterStore] 🛠️ Debug helper available globally: window.__BATEMO_FILTERS__')
  console.log('[FilterStore] 💡 Try: __BATEMO_FILTERS__.info()')
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Range type -> { current signal, full-range signal }. Single source of truth
// for the flat `<type>Min`/`<type>Max` pendingRangeOverrides key convention below.
const RANGE_TYPE_SIGNALS = {
  energy: { range: energyRange, full: fullRangeEnergy },
  power: { range: powerRange, full: fullRangePower },
  capacity: { range: capacityRange, full: fullRangeCapacity },
  current: { range: currentRange, full: fullRangeCurrent },
  cycles: { range: cyclesRange, full: fullRangeCycles },
  year: { range: yearRange, full: fullRangeYear },
  batchVariation: { range: batchVariationRange, full: fullRangeBatchVariation },
  aging: { range: agingRange, full: fullRangeAging },
}

/**
 * Initialize filter ranges from metadata
 * Sets min/max values based on actual cell data
 */
export const initFilterRangesFromMetadata = (metadata) => {
  if (!metadata) return

  // Get current basis (abs, grav, vol) - import from globalSignals
  const currentBasis = basis.value

  console.log('[filterStore] initFilterRangesFromMetadata called:', {
    basis: currentBasis,
    hasEnergyData: !!metadata.energy?.[currentBasis],
    energyBasisData: metadata.energy?.[currentBasis],
    allEnergyData: metadata.energy
  })

  // Energy range - use current basis
  if (metadata.energy?.[currentBasis]) {
    const min = Math.floor(metadata.energy[currentBasis].min || 0)
    const max = Math.ceil(metadata.energy[currentBasis].max || 1000)

    console.log('[filterStore] Setting energy range:', { basis: currentBasis, min, max })

    fullRangeEnergy.value = { min, max }

    // Always update to new range when basis changes
    energyRange.value = { min, max }
  }

  // Power range - use current basis
  if (metadata.power?.[currentBasis]) {
    const min = Math.floor(metadata.power[currentBasis].min || 0)
    const max = Math.ceil(metadata.power[currentBasis].max || 50000)

    fullRangePower.value = { min, max }

    // Always update to new range when basis changes
    powerRange.value = { min, max }
  }

  // Capacity range - use current basis
  if (metadata.capacity?.[currentBasis]) {
    const min = Math.floor(metadata.capacity[currentBasis].min * 10) / 10
    const max = Math.ceil(metadata.capacity[currentBasis].max * 10) / 10

    fullRangeCapacity.value = { min, max }

    // Always update to new range when basis changes
    capacityRange.value = { min, max }
  }

  // Current range - use current basis
  if (metadata.current?.[currentBasis]) {
    const min = Math.floor(metadata.current[currentBasis].min * 10) / 10
    const max = Math.ceil(metadata.current[currentBasis].max * 10) / 10

    fullRangeCurrent.value = { min, max }

    // Always update to new range when basis changes
    currentRange.value = { min, max }
  }

  // Year range
  // If metadata has year data from backend, use it.
  // Otherwise derive from actual loaded cells (backend may not include year in ranges).
  if (metadata.year && metadata.year.min != null && metadata.year.max != null) {
    const min = Math.floor(metadata.year.min)
    const max = Math.ceil(metadata.year.max)

    fullRangeYear.value = { min, max }

    const currentYear = yearRange.value
    if (currentYear.min === 1990 && currentYear.max === 2030) {
      yearRange.value = { min, max }
    }
  } else {
    // Derive year range from loaded cells
    const cells = allCells.value
    if (cells && cells.length > 0) {
      const years = cells
        .map(c => c.year || c.general?.year)
        .filter(y => typeof y === 'number' && y >= 1990 && y <= 2100)
      if (years.length > 0) {
        const min = Math.min(...years)
        const max = Math.max(...years)
        fullRangeYear.value = { min, max }
        yearRange.value = { min, max }
      }
    }
  }

  // Batch Variation range (CAP_spread_std_rel in %)
  if (metadata.batchVariation) {
    const min = Math.floor(metadata.batchVariation.min * 10) / 10
    const max = Math.ceil(metadata.batchVariation.max * 10) / 10

    fullRangeBatchVariation.value = { min, max }

    const currentBatchVar = batchVariationRange.value
    if (currentBatchVar.min === 0 && currentBatchVar.max === 100) {
      batchVariationRange.value = { min, max }
    }
  }

  // Initial Aging range (CAP_loss_rel in %)
  if (metadata.aging) {
    const min = Math.floor(metadata.aging.min * 10) / 10
    const max = Math.ceil(metadata.aging.max * 10) / 10

    fullRangeAging.value = { min, max }

    const currentAging = agingRange.value
    if (currentAging.min === 0 && currentAging.max === 100) {
      agingRange.value = { min, max }
    }
  }

  // Re-apply pending range overrides from presets/commands (survives async metadata reload).
  // Generic across all 8 range types via the flat `<type>Min`/`<type>Max` key convention —
  // only the bounds actually present in the override are applied, the other bound falls back
  // to the freshly loaded full range. This keeps existing producers that emit a single bound
  // (e.g. FreePlotSettingsOverlay's power-min-only preset, :331) working unchanged, while also
  // covering capacity/current/cycles/year/batchVariation/aging, which previously fell straight
  // back to the full range on every basis-triggered reload (silent filter reset).
  const overrides = pendingRangeOverrides.value
  if (overrides) {
    for (const [type, { range: rangeSignal, full: fullSignal }] of Object.entries(RANGE_TYPE_SIGNALS)) {
      const minKey = `${type}Min`
      const maxKey = `${type}Max`
      if (overrides[minKey] == null && overrides[maxKey] == null) continue

      rangeSignal.value = {
        min: overrides[minKey] != null ? overrides[minKey] : fullSignal.value.min,
        max: overrides[maxKey] != null ? overrides[maxKey] : fullSignal.value.max,
      }
    }
    console.log('[FilterStore] Applied pending range overrides:', overrides)
    // One-shot (I1): overrides exist only to survive the SINGLE async metadata
    // reload the basis/domain change (or a preset) just triggered. Clear them
    // now — otherwise a later, unrelated reload re-applies these stale bounds
    // and silently clobbers a range the user changed by hand in between.
    pendingRangeOverrides.value = null
  }

  console.log('[FilterStore] Initialized ranges from metadata:', {
    energy: energyRange.value,
    power: powerRange.value,
    capacity: capacityRange.value,
    current: currentRange.value,
    year: yearRange.value,
    batchVariation: batchVariationRange.value,
    aging: agingRange.value,
    fullRanges: {
      energy: fullRangeEnergy.value,
      power: fullRangePower.value,
      capacity: fullRangeCapacity.value,
      current: fullRangeCurrent.value,
      year: fullRangeYear.value,
      batchVariation: fullRangeBatchVariation.value,
      aging: fullRangeAging.value
    }
  })

  // This function IS the reload FilterPanel.jsx's [basis, domain] effect
  // triggers asynchronously — all of THIS reload's range writes are done, so
  // release commandLayer.js's guard (no-op if nothing armed it; see
  // state/ionReloadGuard.js).
  consumeIonBasisReloadExpectation()
}

/**
 * Initialize filter store: persisted state first, then the URL on top.
 *
 * Called ONCE from `utils/appInitializer.js` (behind its own `isInitialized`
 * guard), not per page mount as the old comment here claimed — nothing called it
 * at all, which is why a cold load ignored the query string entirely. Route
 * changes inside the app do NOT come through here; in-app links apply the URL
 * via `applyNavLink` (components/chat/navLinkUtils.js). Manual filters therefore
 * survive navigation, because the URL never carries them
 * (`updateURLFromFilters` has no callers either).
 */
export const initFilterStore = () => {
  // Load saved preferences from localStorage
  loadFromLocalStorage()

  // Then load filters from URL (overrides localStorage)
  loadFiltersFromURL()

  // Debug: Log active filters after initialization
  console.log('[FilterStore] ✅ Initialized with active filters:', activeFilters.value)
  console.log('[FilterStore] 📊 Current ranges:', {
    energy: energyRange.value,
    power: powerRange.value,
    capacity: capacityRange.value,
    current: currentRange.value
  })
  console.log('[FilterStore] 📊 Full ranges:', {
    energy: fullRangeEnergy.value,
    power: fullRangePower.value,
    capacity: fullRangeCapacity.value,
    current: fullRangeCurrent.value
  })
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // State
  selectedChemistries,
  selectedCathodeChemistries,
  selectedAnodeChemistries,
  selectedManufacturers,
  selectedFormFactors,
  energyRange,
  powerRange,
  capacityRange,
  cyclesRange,
  yearRange,
  searchQuery,
  activePreset,
  showCertifiedOnly,
  showInProductionOnly,
  showFullDataOnly,

  // View state
  currentView,
  displayMode,
  groupBy,
  ragoneColorBy,
  ragoneSizeBy,
  ragoneShowLabels,
  visibleColumns,
  tableDensity,
  selectedRows,
  filterPanelCollapsed,
  expandedSections,

  // Compare state
  panelMode,
  compareCells,

  // Computed
  activeFilters,
  activeFilterCount,
  hasActiveFilters,
  hasCompareCells,
  canCompare,

  // Dynamic Filter Counts (Faceted Search)
  currentFilterState,
  dynamicFilterCounts,
  dynamicRangeData,

  // Actions
  resetAllFilters,
  removeFilter,
  toggleFilter,
  updateRange,
  applyPreset,
  toggleSection,
  isSectionExpanded,
  collapseAllSections,
  expandAllSections,

  // Compare actions
  addToCompare,
  removeFromCompare,
  clearCompare,
  toggleCompare,
  isInCompare,

  // URL & Storage
  updateURLFromFilters,
  loadFiltersFromURL,
  loadFromLocalStorage,
  initFilterStore,
  initFilterRangesFromMetadata
}
